// @flow
import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import io from 'socket.io-client';
import { updateOrderStatusRealtime, setSocketConnection, addRealtimeStatus, addMachineMessage } from '../actions/orders';

type ConnectionState = {
  connected: boolean,
  socket: any,
  error: string | null,
  lastUpdate: Date | null
};

type CommandOptions = {
  timeout?: number,
  data?: any,
  fireAndForget?: boolean
};

type ConnectionOptions = {
  orderId?: string,
  machineImei?: string,
  autoConnect?: boolean,
  subscribeToOrder?: boolean,
  subscribeToMachine?: boolean,
  autoSendStartOrder?: boolean,
  bVendingOrder?: boolean
};

const useRealtimeConnection = (options: ConnectionOptions = {}) => {
  const {
    orderId,
    machineImei,
    autoConnect = true,
    subscribeToOrder = !!orderId,
    subscribeToMachine = !!machineImei,
    autoSendStartOrder = false,
    bVendingOrder = false
  } = options;

  const [connectionState, setConnectionState] = useState<ConnectionState>({
    connected: false,
    socket: null,
    error: null,
    lastUpdate: null
  });

  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const { sessionId } = useSelector((state) => state.auth || {});
  const pendingCommands = useRef(new Map()); // Track pending commands for response matching

  const connectSocket = useCallback(() => {
    if (socketRef.current?.connected) {
      console.log('🔗 Socket already connected');
      return socketRef.current;
    }

    try {
      // Connect to WebSocket server (URL from environment variable)
      const websocketUrl = process.env.REACT_APP_LIVE_URL || 'ws://localhost:4200';
      const socket = io(websocketUrl, {
        transports: ['websocket'],
        timeout: 10000, // 10 second connection timeout
        forceNew: false, // Reuse existing connection if available
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 1000
      });

      socket.on('connect', () => {
        console.log('🔗 Connected to realtime communication server');
        setConnectionState(prev => ({ ...prev, connected: true, error: null }));
        dispatch(setSocketConnection(true));

        // Subscribe to order updates if requested
        if (subscribeToOrder && orderId) {
          console.log(`📋 Subscribing to order updates: ${orderId}`);
          socket.emit('subscribeToOrder', orderId);
        }

        // Subscribe to machine messages if requested
        if (subscribeToMachine && machineImei) {
          console.log(`📱 Subscribing to machine messages: ${machineImei}`);
          socket.emit('subscribeToMachine', machineImei);

          // Send start-order command if requested
          if (autoSendStartOrder && orderId) {
            console.log(`🚀 Sending start-order command (vending: ${!!bVendingOrder})`);
            sendCommand('start-order', {
              timeout: 10,
              data: {
                orderId: orderId,
                is_vending: !!bVendingOrder
              }
            }).catch(error => {
              console.error('🔴 Failed to send start-order:', error);
            });
          }
        }
      });

      // Handle order status updates
      socket.on('orderStatus', (data) => {
        console.log('📡 Received order status update:', data);
        setConnectionState(prev => ({ ...prev, lastUpdate: new Date() }));

        const statusUpdate = {
          orderId: data.order_id || orderId,
          status: data.server_status, // Map server_status to status
          timestamp: data.timestamp || new Date().toISOString(),
          metadata: {
            server_response: data.server_response, // Keep server_response as string
            id: data.id
          },
          machineId: data.machine_id || 'unknown'
        };

        console.log('📤 Dispatching status update:', statusUpdate);

        // Dispatch to Redux for state management
        dispatch(updateOrderStatusRealtime(statusUpdate));
        dispatch(addRealtimeStatus(statusUpdate));
      });

      // Handle machine messages (including command responses)
      socket.on('machineMessage', (data) => {
        console.log('🤖 Received machine message:', data);
        console.log('🔍 Message source:', data.metadata?.source);
        console.log('🔍 Message type:', data.message_type);

        // Skip processing our own commands echoed back from webapp
        if (data.metadata?.source === 'webapp') {
          console.log('⏭️ Skipping webapp-sourced message (command echo)');
          return;
        }

        console.log('✅ Processing Pi/machine message');
        setConnectionState(prev => ({ ...prev, lastUpdate: new Date() }));

        const machineMessage = {
          id: data.id,
          imei: data.imei || machineImei || 'unknown',
          messageType: data.message_type || 'info',
          timestamp: data.timestamp || new Date().toISOString(),
          priority: data.priority || 'normal',
          display: {
            header: data.display_header || data.display?.header || '',
            subHeader: data.display_sub_header || data.display?.sub_header || '',
            showOnUI: data.display_show_on_ui !== 'false' && data.display?.show_on_ui !== false,
            autoDismiss: data.display_auto_dismiss === 'true' || data.display?.auto_dismiss || false,
            dismissAfter: parseInt(data.display_dismiss_after) || data.display?.dismiss_after || 30000
          },
          metadata: data.metadata || {}
        };

        console.log('📤 Dispatching machine message:', machineMessage);
        dispatch(addMachineMessage(machineMessage));

        // Check if this is a response to a pending command
        if (data.message_type === 'command_response' && data.metadata?.request_id) {
          const requestId = data.metadata.request_id;
          console.log('🎯 Command response received for request:', requestId);
          console.log('🔍 Pending commands:', Array.from(pendingCommands.current.keys()));

          const pendingCommand = pendingCommands.current.get(requestId);

          if (pendingCommand) {
            console.log('✅ Found matching pending command, resolving...');
            pendingCommand.resolve(data);
            pendingCommands.current.delete(requestId);
          } else {
            console.log('❌ No matching pending command found for request:', requestId);
          }
        } else {
          console.log('⏭️ Not a command response:', {
            message_type: data.message_type,
            has_request_id: !!data.metadata?.request_id
          });
        }
      });

      socket.on('error', (error) => {
        console.error('🔴 Socket error:', error);
        setConnectionState(prev => ({ ...prev, error: error.message }));
      });

      socket.on('disconnect', (reason) => {
        console.log('🔌 Disconnected from realtime server:', reason);
        setConnectionState(prev => ({ ...prev, connected: false }));
        dispatch(setSocketConnection(false));

        // Reject all pending commands
        pendingCommands.current.forEach((command, requestId) => {
          const disconnectError = new Error('Socket disconnected');
          disconnectError.type = 'CONNECTION_LOST';
          command.reject(disconnectError);
        });
        pendingCommands.current.clear();
      });

      socket.on('connect_error', (error) => {
        console.error('🔴 Connection failed:', error);
        setConnectionState(prev => ({
          ...prev,
          error: 'Connection failed',
          connected: false
        }));
      });

      socketRef.current = socket;
      setConnectionState(prev => ({ ...prev, socket }));
      return socket;

    } catch (error) {
      console.error('🔴 Socket initialization error:', error);
      setConnectionState(prev => ({ ...prev, error: error.message }));
      return null;
    }
  }, [orderId, machineImei, subscribeToOrder, subscribeToMachine, autoSendStartOrder, bVendingOrder, dispatch]);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      console.log('🔌 Disconnecting socket...');

      // Reject all pending commands
      pendingCommands.current.forEach((command, requestId) => {
        const disconnectError = new Error('Socket manually disconnected');
        disconnectError.type = 'CONNECTION_LOST';
        command.reject(disconnectError);
      });
      pendingCommands.current.clear();

      socketRef.current.disconnect();
      socketRef.current = null;
      setConnectionState(prev => ({ ...prev, connected: false, socket: null }));
      dispatch(setSocketConnection(false));
    }
  }, [dispatch]);

  // Generic command sender with built-in timeout and response handling
  const sendCommand = useCallback((command: string, options: CommandOptions = {}) => {
    const socket = socketRef.current;

    if (!socket || !socket.connected) {
      const error = new Error('Socket not connected');
      error.type = 'CONNECTION_ERROR';
      if (options.fireAndForget) {
        console.log(`⚠️ Cannot send fire-and-forget ${command}: Socket not connected`);
        return Promise.resolve(null);
      }
      return Promise.reject(error);
    }

    if (!machineImei) {
      const error = new Error('Machine IMEI required for sending commands');
      error.type = 'CONFIGURATION_ERROR';
      if (options.fireAndForget) {
        console.log(`⚠️ Cannot send fire-and-forget ${command}: No machine IMEI`);
        return Promise.resolve(null);
      }
      return Promise.reject(error);
    }

    const { timeout = 8, data = {}, fireAndForget = false } = options;
    const requestId = `${command}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Fire-and-forget commands: emit and return immediately
    if (fireAndForget) {
      const commandData = {
        channel: machineImei,
        command: command,
        data: {
          request_id: requestId,
          ...data
        },
        timestamp: new Date().toISOString()
      };

      console.log(`🔥 Sending fire-and-forget ${command} command:`, commandData);
      socket.emit('machine-command', commandData);
      return Promise.resolve({ requestId, sent: true, fireAndForget: true });
    }

    // Regular commands: create promise and track response
    return new Promise((resolve, reject) => {
      // Store pending command for response tracking
      pendingCommands.current.set(requestId, { resolve, reject });

      const commandData = {
        channel: machineImei,
        command: command,
        timeout: timeout,
        data: {
          request_id: requestId,
          ...data
        },
        timestamp: new Date().toISOString()
      };

      console.log(`📤 Sending ${command} command:`, commandData);

      // Use Socket.IO's built-in timeout instead of setTimeout
      // Add 2 seconds buffer to avoid edge case where command and websocket timeouts fire simultaneously
      socket.timeout((timeout + 2) * 1000).emit('machine-command', commandData, (err, response) => {
        if (err) {
          // Socket timeout occurred
          console.log(`⏰ Command ${command} timed out after ${timeout} seconds`);
          pendingCommands.current.delete(requestId);
          const timeoutError = new Error(`Command timeout: Machine not reachable`);
          timeoutError.type = 'COMMAND_TIMEOUT';
          reject(timeoutError);
        }
        // If no error, we wait for the actual machine response via machineMessage event
      });
    });
  }, [machineImei]);

  // Specific command helpers for common use cases
  const sendPing = useCallback((options: CommandOptions = {}) => {
    return sendCommand('ping', { timeout: 5, ...options });
  }, [sendCommand]);


  const sendStartOrder = useCallback((orderId: string, isVending: boolean = false, options: CommandOptions = {}) => {
    return sendCommand('start-order', {
      timeout: 10, // Updated to 10 seconds to handle Pi processing time
      data: {
        orderId: orderId,
        is_vending: isVending
      },
      ...options
    });
  }, [sendCommand]);

  const sendProcessOrder = useCallback((orderId: string, isVending: boolean = false, accessCode: string = '') => {
    return sendCommand('process-order', {
      data: {
        orderId: orderId,
        is_vending: isVending,
        accessCode: accessCode
      },
      fireAndForget: true
    });
  }, [sendCommand]);

  // Auto-connect when component mounts if autoConnect is true
  useEffect(() => {
    if (autoConnect && sessionId && (orderId || machineImei)) {
      connectSocket();
    }

    return () => {
      // Only disconnect when component unmounts
      disconnectSocket();
    };
  }, [sessionId]); // Minimal dependencies to avoid unnecessary reconnections

  return {
    // Connection state
    connected: connectionState.connected,
    error: connectionState.error,
    lastUpdate: connectionState.lastUpdate,

    // Connection control
    connect: connectSocket,
    disconnect: disconnectSocket,
    reconnect: connectSocket,

    // Generic command sender
    sendCommand,

    // Specific command helpers
    sendPing,
    sendStartOrder,
    sendProcessOrder
  };
};

export default useRealtimeConnection;
